import { Checkbox } from '@acceso/design-system';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { RelaunchEnrichmentsModal } from '@/components/RelaunchEnrichmentsModal/RelaunchEnrichmentsModal';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { ChannelType } from '@/features/broadcasters/types';
import { GlobalStatusEnrichmentEnum, TSegment } from '@/features/segments/types';
import { useSelectActiveItem } from '@/hooks/useSelectActiveItem';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useSegmentPresence } from '@/providers/websocket';
import { useSelectedItemsStore } from '@/stores/selected-items';

import { BroadcasterScheduleProgramMiniCardCardBody } from './components/BroadcasterScheduleProgramMiniCardCardBody';
import { BroadcasterScheduleProgramMiniCardCardHeader } from './components/BroadcasterScheduleProgramMiniCardCardHeader';
import { useRelaunchCard } from './hook/useRelaunch';

export type BroadcasterScheduleProgramMiniCard = {
  item: TSegment;
  channel?: ChannelType;
  segments?: boolean;
  isActive?: boolean;
  /** The emission this card is listed under, when the view is reviewing one. */
  programBroadcast?: TProgramBroadcast;
};
export function BroadcasterScheduleProgramMiniCardBase({
  item,
  channel,
  segments,
  isActive,
  programBroadcast,
}: BroadcasterScheduleProgramMiniCard) {
  const { t } = useTranslation();
  const { segmentItemsRefs, activeSegment, compactUis, setActiveSegment, setVisibleBox } =
    useBroadcastScheduleProgram();
  const store = useSelectedItemsStore();
  const userConnectedInSegments = useSegmentPresence();
  const { relaunchDialog, handleRelaunchSegment } = useRelaunchCard();
  const { underMinDesktopWith } = useUnderMinDesktopWidth();

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [isActive]);

  const selectSegment = useSelectActiveItem({
    activeItem: activeSegment,
    setActiveItem: setActiveSegment,
  });

  const handleClickItem = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.stopPropagation();
      setVisibleBox(null);
      selectSegment(item);
    },
    [item, selectSegment, setVisibleBox]
  );

  const isDetailActive = useMemo(() => {
    if (!activeSegment) return false;
    return activeSegment.id === `${item.id}`;
  }, [activeSegment, item.id]);

  const handleCheckboxChange = (id: string) => {
    store.dispatch({
      type: 'toggleId',
      payload: id,
    });
  };
  const globalStatusMapper: Record<string, string> = {
    [GlobalStatusEnrichmentEnum.ERROR]: 'bg-feedback-error-20/10',
    [GlobalStatusEnrichmentEnum.PROCESSED]: 'bg-success-light/30',
    [GlobalStatusEnrichmentEnum.PROCESSING]: 'bg-extra-80/10',
  };

  const containerClassName = globalStatusMapper[item.global_status_enrichment];
  const isOccupiedByReviewer = !!userConnectedInSegments[item.id]?.length;

  return (
    <>
      <article
        className={clsx(
          'group/broadcasterCard flex w-full min-w-[256px] cursor-pointer items-start justify-start border-b-[1px] border-neutral-30 pl-2 pt-2 transition-opacity duration-300 hover:bg-black/[.05]',
          !compactUis && 'flex-col',
          isDetailActive &&
            'bg-neutral-15 shadow-[inset_8px_0_0_0_var(--tw-shadow-color)] shadow-accent-active-primary-30',
          store.include.includes(item.id) &&
            'shadow-[inset_8px_0_0_0_var(--tw-shadow-color)] shadow-accent-active-primary-40',
          containerClassName,
          isOccupiedByReviewer && 'reviewer-occupied'
        )}
        onClick={event => handleClickItem(event)}
        ref={el => {
          segmentItemsRefs.current[item.id] = el;
        }}
      >
        <div
          className={clsx(
            'flex w-full items-start gap-2 pr-5',
            underMinDesktopWith && 'flex-col pl-1.5'
          )}
          ref={ref}
        >
          <Checkbox
            onChange={() => {
              handleCheckboxChange(item.id);
            }}
            labelClassName="ml-1"
            checked={store.include.includes(item.id) || store.toggleAllActived}
          />
          <div className={clsx('flex w-full gap-2', !compactUis && 'flex-col')}>
            <BroadcasterScheduleProgramMiniCardCardHeader
              item={item}
              channel={channel}
              programBroadcast={programBroadcast}
            />
            <BroadcasterScheduleProgramMiniCardCardBody
              item={item}
              segments={segments}
              relaunchDialog={relaunchDialog}
            />
          </div>
        </div>
      </article>

      <RelaunchEnrichmentsModal
        isOpen={relaunchDialog.isOpen}
        onClose={relaunchDialog.close}
        onAction={enrichments => handleRelaunchSegment(relaunchDialog.data || '', enrichments)}
        heading={t('Relanzar procesos de enriquecimiento')}
        actionLabel={t('Lanzar procesos')}
        isRadio={item.media_type === 'rts'}
      />
    </>
  );
}

export const BroadcasterScheduleProgramMiniCard = React.memo(
  BroadcasterScheduleProgramMiniCardBase
);
